/**
 * Header behaviour: scroll states, search panel, dropdowns, drawer.
 *
 * All three scroll-derived states share ONE rAF-throttled handler. Two
 * handlers would compete for the same frames, and the React source runs one.
 *
 * Failsafe polarity is inverted from the source on purpose: the CSS default
 * is the solid bar, and this module ADDS .is-at-top for transparency. If this
 * bundle never runs, the header stays solid and readable rather than
 * permanently transparent. Same reasoning as awdd_reveal_gate() — and as of
 * this module, the pre-boot half of that failsafe IS awdd_reveal_gate()'s
 * sibling: awdd_header_gate() (inc/header.php, called inline right after
 * </header>) adds is-at-top to [data-awdd-header] before paint on an
 * over_dark page, then arms its own deadline. window.awddHeaderReady = true
 * below disarms it. It targets is-at-top on the header directly rather than
 * a class of its own, on purpose: that's the one class every
 * group-[.is-at-top]: variant on the four template parts already keys off,
 * so the bar's own background and its descendants' text/logo/icon colours
 * can never end up out of step with each other.
 */

import { setScrollLock } from './scroll-lock.js';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

// Disarms awdd_header_gate()'s deadline (inc/header.php). Set at module top
// level — not inside initHeader() — so it lands as soon as this module
// executes rather than waiting for 'DOMContentLoaded'; a module script is
// deferred, so top-level code here still runs before that event fires,
// buying back some of the gate's deadline budget on a slow connection.
// (This used to sit inside initHeader() with a comment claiming it had to
// run before the header-existence check below in case [data-awdd-header]
// was missing — but awdd_header_gate() itself already returns before it
// ever arms the timer when that element is absent, so there was nothing to
// disarm in that case and the reasoning didn't hold. The actual benefit is
// simply landing earlier.) Mirrors app.js's window.awddRevealReady, which
// is set at module scope for the reveal gate's sibling deadline.
window.awddHeaderReady = true;

export default function initHeader() {
    const header = document.querySelector('[data-awdd-header]');
    if (!header) return;

    // Server-side decision. Absent means the ground is light, so the bar
    // never goes transparent — not even for one frame.
    const overDark = header.hasAttribute('data-awdd-over-dark');

    // ===== Scroll states: one handler =====
    let ticking = false;
    let lastY = window.scrollY;

    const update = () => {
        const y = window.scrollY;

        if (overDark) header.classList.toggle('is-at-top', y <= 24);

        // Hide the utility row on the way down; show it on the way up or
        // near the top.
        if (y < 80 || y < lastY) header.classList.remove('is-util-hidden');
        else if (y > lastY) header.classList.add('is-util-hidden');

        const doc = document.documentElement;
        const max = doc.scrollHeight - doc.clientHeight;
        header.style.setProperty('--awdd-progress', max > 0 ? Math.min(1, y / max).toFixed(4) : '0');

        lastY = y;
        ticking = false;
    };

    const onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });

    // ===== Search panel =====
    const searchToggle = header.querySelector('[data-awdd-search-toggle]');
    const searchPanel = header.querySelector('[data-awdd-search-panel]');
    const searchInput = header.querySelector('[data-awdd-search-input]');
    const searchClose = header.querySelector('[data-awdd-search-close]');

    // The closed panel collapses via max-h-0/overflow-hidden, which is
    // purely visual: neither property takes an element out of the tab order
    // or the accessibility tree, so a keyboard user tabbing past the search
    // toggle used to land in a zero-height input and then a zero-height
    // close button before ever reaching Enquire. `inert` is what actually
    // removes them from both. The panel starts `inert` in the markup
    // (search-panel.php); this clears it on open and restores it on close.
    const setSearch = (open) => {
        header.classList.toggle('is-search-open', open);
        if (searchPanel) searchPanel.inert = !open;
        if (searchToggle) searchToggle.setAttribute('aria-expanded', String(open));
        if (open && searchInput) searchInput.focus();
        else if (!open && searchToggle) searchToggle.focus();
    };

    if (searchToggle) {
        searchToggle.addEventListener('click', () => setSearch(!header.classList.contains('is-search-open')));
    }
    if (searchClose) {
        searchClose.addEventListener('click', () => setSearch(false));
    }

    // ===== Dropdowns =====
    const closeDropdowns = () => {
        header.querySelectorAll('[data-awdd-dropdown-trigger]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
        header.querySelectorAll('[data-awdd-dropdown-panel]').forEach((p) => p.classList.remove('is-open'));
    };

    // Hoisted rather than queried per-dropdown: both the click handler and
    // the hover listeners below need the same answer, and matchMedia's
    // result can't change between iterations of the forEach.
    const hoverCapable = window.matchMedia('(hover: hover)').matches;

    header.querySelectorAll('[data-awdd-dropdown]').forEach((wrap) => {
        const trigger = wrap.querySelector('[data-awdd-dropdown-trigger]');
        const panel = wrap.querySelector('[data-awdd-dropdown-panel]');
        if (!trigger || !panel) return;

        const open = (state) => {
            if (state) closeDropdowns();
            trigger.setAttribute('aria-expanded', String(state));
            panel.classList.toggle('is-open', state);
        };

        // The trigger is a real <a href> to the parent page, so let it behave
        // like one. On a hover-capable device mouseenter has already opened
        // the panel by the time click fires, leaving the click with nothing to
        // do but navigate. This handler used to call preventDefault()
        // unconditionally, which made a parent item with children the one nav
        // item whose own page was unreachable from the bar.
        //
        // Without hover nothing else opens the panel, so the first tap opens
        // it and only a second tap on an already-open trigger navigates.
        trigger.addEventListener('click', (e) => {
            if (hoverCapable) return;
            if (trigger.getAttribute('aria-expanded') === 'true') return;
            e.preventDefault();
            open(true);
        });

        // Keyboard path. Enter on the trigger now navigates instead of
        // opening, so focus has to be what reveals the panel -- it is
        // display:none while closed, which keeps its child links out of the
        // tab order entirely.
        //
        // Gated on :focus-visible because a mouse click and a touch tap both
        // focus the link too. On touch that focus lands before the click
        // handler reads aria-expanded, so an ungated listener would report the
        // panel already open and the first tap would navigate past it.
        wrap.addEventListener('focusin', (e) => {
            if (e.target.matches(':focus-visible')) open(true);
        });

        wrap.addEventListener('focusout', (e) => {
            if (!wrap.contains(e.relatedTarget)) open(false);
        });

        // Hover is an enhancement for pointer devices only; the click and
        // focus paths above work everywhere.
        if (hoverCapable) {
            wrap.addEventListener('mouseenter', () => open(true));
            wrap.addEventListener('mouseleave', () => open(false));
        }
    });

    document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-awdd-dropdown]')) closeDropdowns();
    });

    // ===== Drawer =====
    // Focus trap and inert-while-closed handling moved here from app.js's
    // overlay block rather than rewritten: that logic was already correct.
    const drawerToggle = header.querySelector('[data-awdd-drawer-toggle]');
    const drawer = document.getElementById('awdd-drawer');

    if (drawer) drawer.inert = true;

    const setDrawer = (open) => {
        if (!drawer) return;
        drawer.classList.toggle('is-open', open);
        header.classList.toggle('is-drawer-open', open);
        document.documentElement.classList.toggle('is-drawer-open', open);
        setScrollLock(drawer, open);
        drawer.inert = !open;
        if (drawerToggle) {
            drawerToggle.setAttribute('aria-expanded', String(open));
            drawerToggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        }
        if (open) {
            const first = drawer.querySelectorAll(FOCUSABLE)[0];
            (first || drawer).focus();
        } else if (drawerToggle) {
            drawerToggle.focus();
        }
    };

    if (drawerToggle && drawer) {
        drawerToggle.addEventListener('click', (e) => {
            e.preventDefault();
            setDrawer(!drawer.classList.contains('is-open'));
        });

        drawer.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => setDrawer(false)));

        drawer.addEventListener('keydown', (e) => {
            if (e.key !== 'Tab') return;
            const items = Array.from(drawer.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
            if (!items.length) return;
            const first = items[0];
            const last = items[items.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        });

        // Crossing into desktop hides the drawer in CSS; clear the state so
        // the scroll lock does not survive the breakpoint change.
        const desktop = window.matchMedia('(min-width: 1024px)');
        desktop.addEventListener('change', () => {
            if (desktop.matches && drawer.classList.contains('is-open')) setDrawer(false);
        });
    }

    // ===== Escape closes whatever is open =====
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (drawer && drawer.classList.contains('is-open')) setDrawer(false);
        else if (header.classList.contains('is-search-open')) setSearch(false);
        else closeDropdowns();
    });
}
